/**
 * Nayomi Skincare Chat Widget
 * ---------------------------
 * Self-contained, dependency-free chat bubble that talks to the
 * skincare-ai-chat-backend service and renders the AM/PM routine
 * it returns.
 *
 * Usage — paste before </body> in theme.liquid:
 *
 *   <script
 *     src="https://cdn.yourhost.com/nayomi-chat-widget.js"
 *     data-api-url="https://nayomi-chat.onrender.com/api/chat"
 *     data-shop-domain="nayomi.bg"
 *     defer
 *   ></script>
 *
 * data-api-url    (required) your deployed backend's /api/chat endpoint
 * data-shop-domain (optional) your storefront domain, used to link
 *                  recommended products to their real product pages
 * data-label      (optional) widget title, defaults to "Консултант по грижа за кожата"
 */
(function () {
  "use strict";

  var currentScript = document.currentScript;
  var config = {
    apiUrl: (currentScript && currentScript.getAttribute("data-api-url")) || "",
    shopDomain: (currentScript && currentScript.getAttribute("data-shop-domain")) || "",
    label: (currentScript && currentScript.getAttribute("data-label")) || "Консултант - Найоми",
  };

  if (!config.apiUrl) {
    console.error(
      "[nayomi-chat-widget] Missing data-api-url on the script tag \u2014 " +
        "the widget cannot reach the backend without it."
    );
  }

  var STORAGE_KEY = "nayomiChatState";
  var DAY_LABELS = { AM: "Сутрин", PM: "Вечер", AM_PM: "Сутрин и вечер" };

  // ---------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------
  function injectStyles() {
    var style = document.createElement("style");
    style.textContent =
      "@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');" +
      "#nayomi-chat-widget{" +
      "  --nc-bg:#FFFFFF; --nc-panel-border:#E7E7E7; --nc-text:#212121; --nc-text-muted:#8A8A8A;" +
      "  --nc-accent:#DC3728; --nc-accent-contrast:#FFFFFF; --nc-am:#DC3728; --nc-pm:#3A3A3A;" +
      "  --nc-user-bubble:#F1F1F1; --nc-radius:18px; --nc-shadow:0 12px 40px rgba(33,33,33,.16);" +
      "  font-family:'Poppins',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;" +
      "  position:fixed; bottom:20px; right:20px; z-index:999999;" +
      "}" +
      "#nayomi-chat-widget *{box-sizing:border-box;}" +
      "#nayomi-chat-widget .nc-bubble{" +
      "  width:60px; height:60px; border-radius:50%; background:var(--nc-accent); color:var(--nc-accent-contrast);" +
      "  border:none; cursor:pointer; box-shadow:var(--nc-shadow); display:flex; align-items:center; justify-content:center;" +
      "  transition:transform .15s ease;" +
      "}" +
      "#nayomi-chat-widget .nc-bubble:hover{ transform:scale(1.05); }" +
      "#nayomi-chat-widget .nc-bubble:focus-visible{ outline:2px solid var(--nc-accent); outline-offset:3px; }" +
      "#nayomi-chat-widget .nc-panel{" +
      "  position:fixed; bottom:92px; right:20px; width:380px; max-width:calc(100vw - 32px);" +
      "  height:min(600px, calc(100vh - 120px)); background:var(--nc-bg); border:1px solid var(--nc-panel-border);" +
      "  border-radius:var(--nc-radius); box-shadow:var(--nc-shadow); display:none; flex-direction:column; overflow:hidden;" +
      "}" +
      "#nayomi-chat-widget .nc-panel.nc-open{ display:flex; }" +
      "#nayomi-chat-widget .nc-header{" +
      "  padding:16px 18px; border-bottom:1px solid var(--nc-panel-border); display:flex; align-items:center; justify-content:space-between;" +
      "}" +
      "#nayomi-chat-widget .nc-title{" +
      "  font-weight:600; font-size:16px; color:var(--nc-text); margin:0;" +
      "}" +
      "#nayomi-chat-widget .nc-close{" +
      "  background:none; border:none; color:var(--nc-text-muted); cursor:pointer; font-size:20px; line-height:1; padding:4px;" +
      "}" +
      "#nayomi-chat-widget .nc-close:focus-visible{ outline:2px solid var(--nc-accent); border-radius:4px; }" +
      "#nayomi-chat-widget .nc-messages{" +
      "  flex:1; overflow-y:auto; padding:16px 18px; display:flex; flex-direction:column; gap:12px;" +
      "}" +
      "#nayomi-chat-widget .nc-msg{ max-width:88%; font-size:14px; line-height:1.5; }" +
      "#nayomi-chat-widget .nc-msg.nc-user{" +
      "  align-self:flex-end; background:var(--nc-user-bubble); color:var(--nc-text); padding:9px 13px; border-radius:14px 14px 3px 14px;" +
      "}" +
      "#nayomi-chat-widget .nc-msg.nc-assistant{ align-self:flex-start; color:var(--nc-text); }" +
      "#nayomi-chat-widget .nc-msg.nc-error{ align-self:flex-start; color:#9A4A3F; }" +
      "#nayomi-chat-widget .nc-typing{ align-self:flex-start; display:flex; gap:4px; padding:6px 0; }" +
      "#nayomi-chat-widget .nc-typing span{" +
      "  width:6px; height:6px; border-radius:50%; background:var(--nc-text-muted); opacity:.5; animation:nc-blink 1.2s infinite ease-in-out;" +
      "}" +
      "#nayomi-chat-widget .nc-typing span:nth-child(2){ animation-delay:.2s; }" +
      "#nayomi-chat-widget .nc-typing span:nth-child(3){ animation-delay:.4s; }" +
      "@keyframes nc-blink{ 0%,80%,100%{opacity:.3;} 40%{opacity:1;} }" +
      "@media (prefers-reduced-motion: reduce){ #nayomi-chat-widget .nc-typing span{ animation:none; opacity:.6; } }" +
      "#nayomi-chat-widget .nc-form{" +
      "  border-top:1px solid var(--nc-panel-border); display:flex; gap:8px; padding:12px;" +
      "}" +
      "#nayomi-chat-widget .nc-input{" +
      "  flex:1; resize:none; border:1px solid var(--nc-panel-border); border-radius:12px; padding:9px 12px;" +
      "  font-size:14px; font-family:inherit; color:var(--nc-text); background:#fff; max-height:80px;" +
      "}" +
      "#nayomi-chat-widget .nc-input:focus-visible{ outline:2px solid var(--nc-accent); outline-offset:1px; }" +
      "#nayomi-chat-widget .nc-send{" +
      "  background:var(--nc-accent); color:var(--nc-accent-contrast); border:none; border-radius:12px; padding:0 16px;" +
      "  font-size:14px; cursor:pointer; font-weight:600;" +
      "}" +
      "#nayomi-chat-widget .nc-send:disabled{ opacity:.5; cursor:not-allowed; }" +
      "#nayomi-chat-widget .nc-send:focus-visible{ outline:2px solid var(--nc-accent); outline-offset:2px; }" +
      "#nayomi-chat-widget .nc-routine{" +
      "  border:1px solid var(--nc-panel-border); border-radius:14px; padding:14px; background:#fff; margin-top:4px;" +
      "}" +
      "#nayomi-chat-widget .nc-routine-title{" +
      "  font-weight:600; font-size:15px; margin:0 0 4px;" +
      "}" +
      "#nayomi-chat-widget .nc-routine-summary{ font-size:13px; color:var(--nc-text-muted); margin:0 0 12px; }" +
      "#nayomi-chat-widget .nc-slot{ margin-bottom:12px; }" +
      "#nayomi-chat-widget .nc-slot:last-of-type{ margin-bottom:0; }" +
      "#nayomi-chat-widget .nc-slot-header{" +
      "  display:flex; align-items:center; gap:6px; font-size:12px; font-weight:700; text-transform:uppercase;" +
      "  letter-spacing:.04em; margin-bottom:6px;" +
      "}" +
      "#nayomi-chat-widget .nc-slot-header.nc-am{ color:var(--nc-am); }" +
      "#nayomi-chat-widget .nc-slot-header.nc-pm{ color:var(--nc-pm); }" +
      "#nayomi-chat-widget .nc-step{" +
      "  display:flex; gap:8px; padding:7px 0; border-top:1px solid var(--nc-panel-border);" +
      "}" +
      "#nayomi-chat-widget .nc-step:first-of-type{ border-top:none; }" +
      "#nayomi-chat-widget .nc-step-order{" +
      "  flex-shrink:0; width:18px; height:18px; border-radius:50%; background:var(--nc-bg); color:var(--nc-text-muted);" +
      "  font-size:11px; display:flex; align-items:center; justify-content:center; margin-top:1px;" +
      "}" +
      "#nayomi-chat-widget .nc-step-title{ font-size:13.5px; font-weight:600; margin:0; }" +
      "#nayomi-chat-widget .nc-step-title a{ color:var(--nc-text); text-decoration:underline; text-decoration-color:var(--nc-panel-border); }" +
      "#nayomi-chat-widget .nc-step-meta{ font-size:12px; color:var(--nc-text-muted); margin:1px 0 3px; }" +
      "#nayomi-chat-widget .nc-step-instructions{ font-size:12.5px; color:var(--nc-text); margin:0; }" +
      "#nayomi-chat-widget .nc-callout{" +
      "  font-size:12px; background:var(--nc-bg); border-radius:10px; padding:8px 10px; margin-top:10px; color:var(--nc-text);" +
      "}" +
      "#nayomi-chat-widget .nc-disclaimer{ font-size:11px; color:var(--nc-text-muted); margin-top:10px; }" +
      "@media (max-width:480px){" +
      "  #nayomi-chat-widget .nc-panel{ right:16px; bottom:88px; width:calc(100vw - 32px); }" +
      "}";
    document.head.appendChild(style);
  }

  // ---------------------------------------------------------------------
  // Icons (minimal inline SVG, no external requests)
  // ---------------------------------------------------------------------
  var ICON_BUBBLE =
    '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M12 2C8 6 6 9 6 12.5C6 16.6 8.7 20 12 20C15.3 20 18 16.6 18 12.5C18 9 16 6 12 2Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
    '<path d="M12 20V11" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
    "</svg>";
  var ICON_SUN =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<circle cx="12" cy="12" r="4.5" stroke="currentColor" stroke-width="1.8"/>' +
    '<path d="M12 2.5V5M12 19V21.5M4.2 4.2L6 6M18 18L19.8 19.8M2.5 12H5M19 12H21.5M4.2 19.8L6 18M18 6L19.8 4.2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    "</svg>";
  var ICON_MOON =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M20 13.6A8.5 8.5 0 1 1 10.4 4a7 7 0 0 0 9.6 9.6Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>' +
    "</svg>";

  // ---------------------------------------------------------------------
  // State persistence (sessionStorage — survives page navigation within
  // a visit, clears when the browser tab closes)
  // ---------------------------------------------------------------------
  function loadState() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return { history: [], displayLog: [] };
      var parsed = JSON.parse(raw);
      return {
        history: Array.isArray(parsed.history) ? parsed.history : [],
        displayLog: Array.isArray(parsed.displayLog) ? parsed.displayLog : [],
      };
    } catch (e) {
      return { history: [], displayLog: [] };
    }
  }

  function saveState(state) {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ history: state.history, displayLog: state.displayLog })
      );
    } catch (e) {
      /* storage unavailable — conversation just won't survive navigation */
    }
  }

  // ---------------------------------------------------------------------
  // DOM scaffold
  // ---------------------------------------------------------------------
  function buildWidget() {
    var root = document.createElement("div");
    root.id = "nayomi-chat-widget";

    var bubble = document.createElement("button");
    bubble.className = "nc-bubble";
    bubble.type = "button";
    bubble.setAttribute("aria-label", "Отвори " + config.label);
    bubble.innerHTML = ICON_BUBBLE;

    var panel = document.createElement("div");
    panel.className = "nc-panel";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", config.label);

    var header = document.createElement("div");
    header.className = "nc-header";
    var title = document.createElement("p");
    title.className = "nc-title";
    title.textContent = config.label;
    var closeBtn = document.createElement("button");
    closeBtn.className = "nc-close";
    closeBtn.type = "button";
    closeBtn.setAttribute("aria-label", "Затвори чата");
    closeBtn.innerHTML = "&times;";
    header.appendChild(title);
    header.appendChild(closeBtn);

    var messages = document.createElement("div");
    messages.className = "nc-messages";

    var form = document.createElement("form");
    form.className = "nc-form";
    var input = document.createElement("textarea");
    input.className = "nc-input";
    input.rows = 1;
    input.placeholder = "Разкажете ни за вашата кожа\u2026";
    input.setAttribute("aria-label", "Съобщение");
    var sendBtn = document.createElement("button");
    sendBtn.className = "nc-send";
    sendBtn.type = "submit";
    sendBtn.textContent = "Изпрати";
    form.appendChild(input);
    form.appendChild(sendBtn);

    panel.appendChild(header);
    panel.appendChild(messages);
    panel.appendChild(form);
    root.appendChild(panel);
    root.appendChild(bubble);
    document.body.appendChild(root);

    return {
      root: root,
      bubble: bubble,
      panel: panel,
      closeBtn: closeBtn,
      messages: messages,
      form: form,
      input: input,
      sendBtn: sendBtn,
    };
  }

  // ---------------------------------------------------------------------
  // Rendering helpers (all use textContent for user/model text — never
  // innerHTML with untrusted content, to avoid XSS)
  // ---------------------------------------------------------------------
  function scrollToBottom(el) {
    el.scrollTop = el.scrollHeight;
  }

  function appendUserMessage(container, text) {
    var el = document.createElement("div");
    el.className = "nc-msg nc-user";
    el.textContent = text;
    container.appendChild(el);
    scrollToBottom(container);
  }

  function appendAssistantMessage(container, text) {
    var el = document.createElement("div");
    el.className = "nc-msg nc-assistant";
    el.textContent = text;
    container.appendChild(el);
    scrollToBottom(container);
  }

  function appendErrorMessage(container, text) {
    var el = document.createElement("div");
    el.className = "nc-msg nc-error";
    el.textContent = text;
    container.appendChild(el);
    scrollToBottom(container);
  }

  function appendTypingIndicator(container) {
    var el = document.createElement("div");
    el.className = "nc-typing";
    el.setAttribute("aria-label", "Мисли");
    el.innerHTML = "<span></span><span></span><span></span>";
    container.appendChild(el);
    scrollToBottom(container);
    return el;
  }

  function buildProductUrl(handle) {
    if (!config.shopDomain || !handle) return null;
    return "https://" + config.shopDomain.replace(/^https?:\/\//, "") + "/products/" + handle;
  }

  function appendRoutineCard(container, routine) {
    var card = document.createElement("div");
    card.className = "nc-routine";

    var title = document.createElement("p");
    title.className = "nc-routine-title";
    title.textContent = "Вашата рутина";
    card.appendChild(title);

    if (routine.summary) {
      var summary = document.createElement("p");
      summary.className = "nc-routine-summary";
      summary.textContent = routine.summary;
      card.appendChild(summary);
    }

    var steps = Array.isArray(routine.steps) ? routine.steps.slice() : [];
    var groups = { AM: [], PM: [], AM_PM: [] };
    steps.forEach(function (step) {
      var key = groups[step.time_of_day] ? step.time_of_day : "AM_PM";
      groups[key].push(step);
    });
    ["AM", "PM", "AM_PM"].forEach(function (key) {
      if (!groups[key].length) return;
      groups[key].sort(function (a, b) {
        return (a.order || 0) - (b.order || 0);
      });

      var slot = document.createElement("div");
      slot.className = "nc-slot";

      var slotHeader = document.createElement("div");
      slotHeader.className = "nc-slot-header " + (key === "PM" ? "nc-pm" : "nc-am");
      slotHeader.innerHTML = (key === "PM" ? ICON_MOON : ICON_SUN) + " <span></span>";
      slotHeader.querySelector("span").textContent = DAY_LABELS[key];
      slot.appendChild(slotHeader);

      groups[key].forEach(function (step) {
        var stepEl = document.createElement("div");
        stepEl.className = "nc-step";

        var orderEl = document.createElement("div");
        orderEl.className = "nc-step-order";
        orderEl.textContent = String(step.order || "\u2022");

        var body = document.createElement("div");

        var stepTitle = document.createElement("p");
        stepTitle.className = "nc-step-title";
        var productUrl = buildProductUrl(step.product_handle);
        if (productUrl) {
          var link = document.createElement("a");
          link.href = productUrl;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.textContent = step.product_title || step.product_handle || "Продукт";
          stepTitle.appendChild(link);
        } else {
          stepTitle.textContent = step.product_title || step.product_handle || "Продукт";
        }
        body.appendChild(stepTitle);

        if (step.frequency) {
          var meta = document.createElement("p");
          meta.className = "nc-step-meta";
          meta.textContent = step.frequency;
          body.appendChild(meta);
        }

        if (step.instructions) {
          var instr = document.createElement("p");
          instr.className = "nc-step-instructions";
          instr.textContent = step.instructions;
          body.appendChild(instr);
        }

        stepEl.appendChild(orderEl);
        stepEl.appendChild(body);
        slot.appendChild(stepEl);
      });

      card.appendChild(slot);
    });

    if (routine.patch_test_recommended || (routine.conflict_warnings && routine.conflict_warnings.length)) {
      var callout = document.createElement("div");
      callout.className = "nc-callout";
      var lines = [];
      if (routine.patch_test_recommended) {
        lines.push("Тествайте всеки нов продукт за 24\u201348 часа преди пълно използване.");
      }
      (routine.conflict_warnings || []).forEach(function (w) {
        lines.push(w);
      });
      callout.textContent = lines.join(" ");
      card.appendChild(callout);
    }

    if (routine.disclaimer) {
      var disclaimer = document.createElement("p");
      disclaimer.className = "nc-disclaimer";
      disclaimer.textContent = routine.disclaimer;
      card.appendChild(disclaimer);
    }

    container.appendChild(card);
    scrollToBottom(container);
  }

  // ---------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------
  function init() {
    injectStyles();
    var els = buildWidget();
    var state = loadState();

    // Replay any conversation from earlier in this browsing session.
    state.displayLog.forEach(function (entry) {
      if (entry.role === "user") {
        appendUserMessage(els.messages, entry.text);
      } else {
        if (entry.text) appendAssistantMessage(els.messages, entry.text);
        if (entry.routine) appendRoutineCard(els.messages, entry.routine);
      }
    });

    function setOpen(open) {
      state.open = open;
      els.panel.classList.toggle("nc-open", open);
      els.bubble.setAttribute("aria-expanded", open ? "true" : "false");
      if (open) {
        els.input.focus();
        if (!state.greeted) {
          state.greeted = true;
          var greeting =
            "Здравейте! Аз съм вашият консултант по грижа за кожата — помагам ви да намерите подходящите продукти от нашия каталог и да изградите рутина, съобразена с вашата кожа. " +
            "Имате ли конкретен проблем с кожата, с който да помогна, или просто разглеждате и търсите нещо интересно?";
          appendAssistantMessage(els.messages, greeting);
          state.displayLog.push({ role: "assistant", text: greeting, routine: null });
          saveState(state);
        }
      }
    }

    els.bubble.addEventListener("click", function () {
      setOpen(!state.open);
    });
    els.closeBtn.addEventListener("click", function () {
      setOpen(false);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && state.open) setOpen(false);
    });

    // Auto-grow the textarea up to the CSS max-height.
    els.input.addEventListener("input", function () {
      els.input.style.height = "auto";
      els.input.style.height = els.input.scrollHeight + "px";
    });
    els.input.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        els.form.requestSubmit();
      }
    });

    function setLoadingUI(loading) {
      els.sendBtn.disabled = loading;
      els.input.disabled = loading;
    }

    els.form.addEventListener("submit", function (e) {
      e.preventDefault();
      var text = els.input.value.trim();
      if (!text || state.loading) return;

      if (!config.apiUrl) {
        appendErrorMessage(els.messages, "Този чат все още не е конфигуриран — липсва URL адрес на бекенда.");
        return;
      }

      appendUserMessage(els.messages, text);
      state.displayLog.push({ role: "user", text: text });
      els.input.value = "";
      els.input.style.height = "auto";

      state.loading = true;
      setLoadingUI(true);
      var typingEl = appendTypingIndicator(els.messages);

      fetch(config.apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, history: state.history }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Request failed with status " + res.status);
          return res.json();
        })
        .then(function (data) {
          typingEl.remove();
          state.history = data.history || state.history;
          if (data.reply) appendAssistantMessage(els.messages, data.reply);
          if (data.routine) appendRoutineCard(els.messages, data.routine);
          state.displayLog.push({
            role: "assistant",
            text: data.reply || "",
            routine: data.routine || null,
          });
          saveState(state);
        })
        .catch(function (err) {
          typingEl.remove();
          console.error("[nayomi-chat-widget]", err);
          appendErrorMessage(
            els.messages,
            "Възникна проблем при свързването с чата \u2014 моля, опитайте отново след малко."
          );
        })
        .finally(function () {
          state.loading = false;
          setLoadingUI(false);
        });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
